import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GlobalActivityPill } from './GlobalActivityPill.js';
import { EvoLogo } from './common/EvoLogo.js';
import { Icon } from './Icon.js';
import { useTopBarCtx } from '../contexts/TopBarContext.js';
import { useSession } from '../contexts/SessionContext.js';
import i18n from '../i18n.js';

const SIDEBAR_W = 200;
const SIDEBAR_W_COLLAPSED = 80;

function greetingKey(): 'morning' | 'afternoon' | 'evening' {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 19) return 'afternoon';
  return 'evening';
}

interface TopBarProps {
  collapsed: boolean;
}

export function TopBar({ collapsed }: TopBarProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { override } = useTopBarCtx();
  const { displayName } = useSession();
  const sidebarW = collapsed ? SIDEBAR_W_COLLAPSED : SIDEBAR_W;

  const dateStr = new Date().toLocaleDateString(i18n.language, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  let defaultTitle: string | null = null;
  if (pathname === '/') {
    defaultTitle = t(`home.greeting.${greetingKey()}`, { name: displayName ?? '' });
  }

  const title = override.title ?? defaultTitle;
  const subtitle = override.subtitle;
  const heroIcon = override.heroIcon;
  const heroIconColor = override.heroIconColor ?? 'var(--evo-cyan)';
  const bottomActions = override.bottomActions;

  return (
    <header
      style={{
        display: 'grid',
        gridTemplateColumns: `${sidebarW}px 1fr`,
        height: '152px',
        borderBottom: '1px solid var(--evo-stroke)',
        flexShrink: 0,
        transition: 'grid-template-columns 180ms ease',
      }}
    >
      {/* Logo column — mirrors sidebar width, vertically centered */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: collapsed ? '0 8px' : '0 16px',
          borderRight: '1px solid var(--evo-stroke)',
          transition: 'padding 180ms ease',
        }}
      >
        <EvoLogo size="md" iconOnly={collapsed} />
      </div>

      {/* Content column */}
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {/* Row 1: fixed 52px — date left, cost meter right */}
        <div
          style={{
            height: '52px',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 var(--evo-content-pad-x)',
          }}
        >
          <span
            style={{
              fontSize: '13px',
              lineHeight: 1.5,
              color: 'var(--evo-fg-3)',
              textTransform: 'capitalize',
            }}
          >
            {dateStr}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <GlobalActivityPill />
          </div>
        </div>

        {/* Row 2: fixed 100px — optional icon + title left, actions right */}
        <div
          style={{
            height: '100px',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            padding: '0 var(--evo-content-pad-x)',
            gap: 16,
          }}
        >
          {heroIcon && (
            <span
              style={{
                flexShrink: 0,
                lineHeight: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: heroIconColor,
              }}
            >
              <Icon name={heroIcon} size={56} strokeWidth={1.5} />
            </span>
          )}

          {title && (
            <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
              <h1
                style={{
                  margin: 0,
                  fontFamily: 'var(--evo-font)',
                  fontSize: '45px',
                  lineHeight: 1,
                  color: 'var(--evo-fg-1)',
                  fontWeight: 400,
                  letterSpacing: '-0.01em',
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {title}
              </h1>
              {subtitle && (
                <p style={{
                  margin: '4px 0 0',
                  fontSize: '13px',
                  color: 'var(--evo-fg-3)',
                  fontFamily: 'var(--evo-font)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                  {subtitle}
                </p>
              )}
            </div>
          )}

          {bottomActions && (
            <div style={{ marginLeft: 'auto', flexShrink: 0 }}>
              {bottomActions}
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
